import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';
import { navLinks } from '../data/partners';
import { useActiveSection } from '../hooks/useActiveSection';
import { SITE_NAME } from '../data/site';
const sectionIds = navLinks.map(l => l.href.slice(1));
export function Navbar() {
  const [open, setOpen] = useState(false);
  const active = useActiveSection(sectionIds);
  const {
    scrollYProgress
  } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 30,
    restDelta: 0.001
  });
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(93);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => setBarHeight(el.offsetHeight + 1);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <>
    {/* Spacer reserves the fixed header's height so content isn't hidden beneath it */}
    <div style={{
      height: barHeight
    }} aria-hidden="true" data-id="element-206" data-mp="epdknphf8ns6" />
    <header className="fixed inset-x-0 top-0 z-50 w-full border-b border-ink/10 bg-sand/90 backdrop-blur" data-id="element-207" data-mp="jnylnr4hzbho">
      <div ref={barRef} data-id="element-208" data-mp="ol8dtej9u197">
      <div className="h-8 bg-ink" data-id="element-209" data-mp="oje1uppz9jh9">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-end px-4 sm:px-5 md:px-8" data-id="element-210" data-mp="3n45gd84xpyeb">
          <LanguageToggle data-id="element-211" data-mp="3s1ntlapzizs4" />
        </div>
      </div>
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-5 md:px-8" aria-label="Main" data-id="element-212" data-mp="3s0cqld4d5w22">
        <a href="#top" className="group flex min-w-0 items-center gap-2 sm:gap-3" aria-label="Hondurans in LA home" data-id="element-213" data-mp="5n4h5jkkluoa">
          <Logo className="w-12 shrink-0 transition-transform duration-200 ease-out group-hover:-rotate-6 group-hover:scale-105 sm:w-16" data-id="element-214" data-mp="34c38al6czqfo" />
          <span translate="no" className="notranslate truncate font-display text-base uppercase tracking-wide text-ink sm:text-xl" data-id="element-215" data-mp="399mm5jz5a173">
            {SITE_NAME}
          </span>
        </a>

        <ul className="hidden items-center gap-8 lg:flex" data-id="element-216" data-mp="2wa40yhgq7arx">
          {navLinks.map(link => {
              const isActive = active === link.href.slice(1);
              return <li key={link.href} data-id="element-217" data-mp="362i2pa7t0he3">
                <a href={link.href} aria-current={isActive ? 'true' : undefined} className={`relative whitespace-nowrap text-sm font-bold transition-colors duration-150 hover:text-sunset ${isActive ? 'text-ink' : 'text-ink/60'}`} data-id="element-218" data-mp="2icffb7eu3zlt">
                  {link.label}
                  {isActive && <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 right-0 h-[3px] rounded-full bg-sunset" transition={{
                    duration: 0.25,
                    ease: [0.23, 1, 0.32, 1]
                  }} data-id="element-219" data-mp="7k441teyh840" />}
                </a>
              </li>;
            })}
        </ul>

        <div className="flex items-center gap-2" data-id="element-220" data-mp="f1is478a3cog">
          <a href="#join" className="whitespace-nowrap rounded-full bg-sunset px-5 py-2.5 text-sm font-bold text-ink transition-[transform,background-color] duration-150 ease-out hover:bg-sun active:scale-[0.97]" data-id="element-221" data-mp="155nhyouf5re">
            Donate
          </a>
          <button type="button" className="rounded-full p-2.5 text-ink transition-colors duration-150 hover:bg-dune lg:hidden" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} data-id="element-222" data-mp="xuwehnxxeasg">
            {open ? <XIcon size={22} data-id="element-223" data-mp="1bsutmvk2mb90" /> : <MenuIcon size={22} data-id="element-224" data-mp="1bsbcvi65pswk" />}
          </button>
        </div>
      </nav>
      </div>

      <motion.div className="absolute bottom-0 left-0 right-0 h-1 origin-left bg-sunset" style={{
        scaleX: progress
      }} aria-hidden="true" data-id="element-225" data-mp="1gmpa7q4dqa3v" />

      <AnimatePresence data-id="element-226" data-mp="3k19cttg9ez3a">
        {open && <motion.div id="mobile-menu" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="border-t border-ink/10 bg-sand lg:hidden" data-id="element-227" data-mp="3f3r9cil0hc2o">
            <ul className="mx-auto flex max-w-7xl flex-col px-5 py-4" data-id="element-228" data-mp="6no56mt3y5j3">
              {navLinks.map(link => <li key={link.href} data-id="element-229" data-mp="1vwod3zr7e65">
                  <a href={link.href} onClick={() => setOpen(false)} className="block py-3 font-display text-2xl uppercase text-ink transition-colors duration-150 hover:text-sunset" data-id="element-230" data-mp="1uuznwptet5y">
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </header>
    </>;
}